import { App as AntdApp, Button, Form, Input, Modal, Select, Space, Table, Tag, Typography, message } from "antd";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusOutlined } from "@ant-design/icons";
import type {
  WhitelistAddResponse,
  WhitelistEntry,
  WhitelistEntryInput,
  WhitelistListResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../../lib/apiRequest";
import { errorMessage, errorCode } from "../../lib/errors";
import { GaCancelledError } from "../../lib/ga-interceptor";

const { Text } = Typography;

/** From-whitelist add/delete (docs/305 §3.5). Both are step-up ops; delete is
 * guarded against removing the entry that backs the tenant's default_from (409). */
export default function WhitelistTab({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { modal } = AntdApp.useApp();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<WhitelistEntryInput>();

  const listQuery = useQuery({
    queryKey: ["whitelist", tenantId],
    queryFn: () => apiRequest<WhitelistListResponse>(`/tenants/${tenantId}/whitelist`),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["whitelist", tenantId] });
    void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
  };

  const addMutation = useMutation({
    mutationFn: (body: WhitelistEntryInput) =>
      apiRequest<WhitelistAddResponse>(`/tenants/${tenantId}/whitelist`, { method: "POST", body }),
    onSuccess: () => {
      message.success("Whitelist entry added");
      setOpen(false);
      form.resetFields();
      invalidate();
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to add entry"));
    },
  });

  const remove = (entry: WhitelistEntry): void => {
    modal.confirm({
      title: "Delete whitelist entry?",
      content: `${entry.kind}: ${entry.value}. Requires step-up.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiRequest(`/whitelist/${entry.id}`, { method: "DELETE" });
          message.success("Entry deleted");
          invalidate();
        } catch (err) {
          if (err instanceof GaCancelledError) return;
          if (errorCode(err) === "WHITELIST_REFERENCED_BY_DEFAULT_FROM") {
            modal.error({
              title: "Cannot delete this entry",
              content:
                "This entry currently authorizes the tenant's default_from. Change tenant.default_from first, then delete it.",
            });
            return;
          }
          message.error(errorMessage(err, "Failed to delete entry"));
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        Add entry
      </Button>

      <Table<WhitelistEntry>
        rowKey="id"
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.entries ?? []}
        pagination={false}
        columns={[
          {
            title: "Kind",
            dataIndex: "kind",
            key: "kind",
            render: (k: WhitelistEntry["kind"]) => <Tag>{k}</Tag>,
          },
          { title: "Value", dataIndex: "value", key: "value", render: (v: string) => <Text code>{v}</Text> },
          {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, entry) => (
              <Button size="small" danger onClick={() => remove(entry)}>
                Delete
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title="Add whitelist entry"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText="Add"
        confirmLoading={addMutation.isPending}
        destroyOnClose
      >
        <Form<WhitelistEntryInput>
          form={form}
          layout="vertical"
          onFinish={(v) => addMutation.mutate(v)}
          initialValues={{ kind: "domain" }}
          preserve={false}
        >
          <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "domain", label: "domain" },
                { value: "address", label: "address" },
              ]}
            />
          </Form.Item>
          <Form.Item name="value" label="Value" rules={[{ required: true, message: "Value is required" }]}>
            <Input placeholder="acme.com or noreply@acme.com" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
